import React from "react";
import { Plus, Flame } from "lucide-react";
import { useCart } from "../contexts/CartContext";

const SPICE_HINT = {
  arrabbiata: 1,
  gochujang: 2,
  xstreamely_hot: 3,
};

function AllergenChips({ allergens, labels }) {
  if (!allergens || allergens.length === 0) {
    return (
      <div className="mt-3 inline-flex text-cream/40 text-[10px] font-heading uppercase tracking-widest">
        No flagged allergens
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap gap-1" data-testid="allergen-chips">
      {allergens.map((a) => (
        <span
          key={a}
          className="px-1.5 py-0.5 border border-amber-700/60 text-amber-300/90 text-[10px] font-heading uppercase tracking-widest"
        >
          {labels?.[a] || a}
        </span>
      ))}
    </div>
  );
}

function ItemCard({ item, allergenLabels }) {
  const { addItem } = useCart();
  const spice = SPICE_HINT[item.flavour] || 0;
  return (
    <div
      className="poster-card p-5 flex flex-col justify-between min-h-[140px] hover:border-gold transition-colors"
      data-testid={`menu-item-${item.id}`}
    >
      <div>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading uppercase tracking-tight text-2xl text-cream leading-none">
            {item.name}
          </h3>
          <div className="font-heading text-gold text-2xl leading-none">
            £{item.price.toFixed(2)}
          </div>
        </div>
        {item.desc && <p className="mt-2 text-cream/70 text-sm">{item.desc}</p>}
        {spice > 0 && (
          <div className="mt-2 inline-flex items-center gap-1 text-accent text-xs font-heading uppercase tracking-widest">
            {Array.from({ length: spice }).map((_, i) => (
              <Flame key={i} className="h-3 w-3" />
            ))}
            {item.flavour === "xstreamely_hot" && <span className="ml-1">Xstreamely Hot</span>}
          </div>
        )}
        <AllergenChips allergens={item.allergens} labels={allergenLabels} />
      </div>
      <button
        onClick={() =>
          addItem({
            is_combo: false,
            item_id: item.id,
            name: item.name,
            unit_price: item.price,
            quantity: 1,
          })
        }
        className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-2 font-heading uppercase tracking-wider text-sm border-2 border-cream/40 text-cream hover:border-gold hover:text-gold transition-colors"
        data-testid={`add-${item.id}-btn`}
      >
        <Plus className="h-4 w-4" /> Add
      </button>
    </div>
  );
}

export default function MenuSection({ title, items, columns = 2, id, allergenLabels }) {
  return (
    <section id={id} className="border-b border-border" data-testid={`menu-section-${id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-heading uppercase tracking-tight text-cream text-4xl sm:text-5xl">
            {title}
          </h2>
          <div className="hidden sm:block h-px flex-1 bg-border ml-6" />
        </div>
        <div className={`grid gap-4 ${columns === 3 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"}`}>
          {items.map((it) => (
            <ItemCard key={it.id} item={it} allergenLabels={allergenLabels} />
          ))}
        </div>
      </div>
    </section>
  );
}
